import { useEffect, useState } from 'react';
import { apiGet, apiPut, ApiError } from '../lib/apiClient';
import { useApiResource } from '../lib/useApiResource';
import { StatusBadge } from '../components/StatusBadge';
import { DataTable } from '../components/DataTable';
import { Checkbox } from '../components/FormCheck';

/** GET/PUT /admin/service-rules/catalog (ServiceCatalogRulesService.getEffectiveCatalog). */
interface CatalogPair {
  category: string;
  service: string;
  basePriceUsd: number;
  concurrency: 'blocking' | 'non_blocking';
  enabled: boolean;
}

/** GET/PUT /admin/service-rules/concurrency (ServiceConcurrencyRulesService.getEffectiveRules). */
interface ConcurrencyRule {
  serviceType: string;
  defaultConcurrencyClass: 'blocking' | 'non_blocking';
  maxConcurrentPerSpecialist: number | null;
}

/** GET /admin/service-rules/eligibility — read-only (ServiceEligibilityService.getEffectiveRules). */
interface EligibilityRule {
  rule: string;
  description: string;
  configurable: boolean;
}
interface EligibilityResponse {
  asOf: string;
  rules: EligibilityRule[];
}

type ActiveTab = 'catalog' | 'concurrency' | 'eligibility';

const pairKey = (category: string, service: string) => `${category}::${service}`;

/**
 * ADM ops — Service Rules (Task E), 3 tabs:
 *  - Catalog: (category, service) matrix, modeled after RolesPermissionsPage's togglable-pairs
 *    matrix. PUT is a full-replace of the enabled set, so every toggle sends the complete
 *    enabledPairs list, not a diff.
 *  - Concurrency: per-service-type cap, editable number input per row. NOT yet enforced
 *    anywhere in the matching engine — admin-visible/editable config only, labeled as such.
 *  - Eligibility: read-only info panel, no form controls.
 */
export function ServiceRulesPage() {
  const [activeTab, setActiveTab] = useState<ActiveTab>('catalog');

  return (
    <div>
      <div className="section-header">
        <div>
          <h1>Service Rules</h1>
          <p>Bookable service catalog, per-specialist concurrency caps, and the current matching/eligibility rules.</p>
        </div>
      </div>

      <div className="tabs">
        {(['catalog', 'concurrency', 'eligibility'] as const).map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`tab-btn ${activeTab === tab ? 'active' : ''}`}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      {activeTab === 'catalog' && <CatalogTab />}
      {activeTab === 'concurrency' && <ConcurrencyTab />}
      {activeTab === 'eligibility' && <EligibilityTab />}
    </div>
  );
}

function CatalogTab() {
  const { data: pairs, loading, error, reload } = useApiResource(
    () => apiGet<CatalogPair[]>('/admin/service-rules/catalog'),
    [],
  );

  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [pendingOverrides, setPendingOverrides] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (!pairs || Object.keys(pendingOverrides).length === 0) return;
    setPendingOverrides((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const key of Object.keys(prev)) {
        const pair = pairs.find((p) => pairKey(p.category, p.service) === key);
        if (pair && pair.enabled === prev[key]) {
          delete next[key];
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [pairs, pendingOverrides]);

  const togglePair = async (target: CatalogPair, checked: boolean) => {
    if (!pairs) return;
    const key = pairKey(target.category, target.service);
    setPendingOverrides((prev) => ({ ...prev, [key]: checked }));
    setBusyKey(key);
    setActionError(null);
    try {
      const enabledPairs = pairs
        .map((p) => ({
          category: p.category,
          service: p.service,
          enabled: pairKey(p.category, p.service) === key ? checked : p.enabled,
        }))
        .filter((p) => p.enabled)
        .map(({ category, service }) => ({ category, service }));
      await apiPut('/admin/service-rules/catalog', { enabledPairs });
      reload();
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : 'Catalog rule update failed');
      setPendingOverrides((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    } finally {
      setBusyKey(null);
    }
  };

  const showLoadingState = !pairs && loading;
  const categories = Array.from(new Set((pairs ?? []).map((p) => p.category)));
  const services = Array.from(new Set((pairs ?? []).map((p) => p.service)));

  return (
    <div>
      {error && <div className="error-banner">{error}</div>}
      {actionError && <div className="error-banner">{actionError}</div>}

      <div className="table-wrap">
        {showLoadingState ? (
          <div className="empty-state">Loading…</div>
        ) : !pairs || pairs.length === 0 ? (
          <div className="empty-state">No catalog pairs found.</div>
        ) : (
          <table className="data-table dt-table">
            <thead>
              <tr>
                <th>Service \ Category</th>
                {categories.map((cat) => (
                  <th key={cat} style={{ textAlign: 'center' }}>
                    {cat}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {services.map((service) => (
                <tr key={service}>
                  <td data-label="Service">{service}</td>
                  {categories.map((category) => {
                    const pair = pairs.find((p) => p.category === category && p.service === service);
                    if (!pair) {
                      return (
                        <td key={category} data-label={category} style={{ textAlign: 'center', color: 'var(--text-muted)' }}>
                          —
                        </td>
                      );
                    }
                    const key = pairKey(category, service);
                    const checked = key in pendingOverrides ? pendingOverrides[key] : pair.enabled;
                    return (
                      <td key={category} data-label={category} style={{ textAlign: 'center' }}>
                        <Checkbox
                          circle
                          checked={checked}
                          disabled={busyKey === key}
                          ariaLabel={`${service} in ${category}`}
                          onChange={(next) => void togglePair(pair, next)}
                        />
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function ConcurrencyTab() {
  const { data: rules, loading, error, reload } = useApiResource(
    () => apiGet<ConcurrencyRule[]>('/admin/service-rules/concurrency'),
    [],
  );

  const [busyType, setBusyType] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<Record<string, string>>({});

  const draftFor = (rule: ConcurrencyRule) =>
    rule.serviceType in drafts ? drafts[rule.serviceType] : rule.maxConcurrentPerSpecialist?.toString() ?? '';

  const saveRule = async (rule: ConcurrencyRule) => {
    const raw = draftFor(rule).trim();
    const maxConcurrentPerSpecialist = raw === '' ? null : Number(raw);
    if (maxConcurrentPerSpecialist !== null && (!Number.isFinite(maxConcurrentPerSpecialist) || maxConcurrentPerSpecialist <= 0)) {
      setActionError('Cap must be a positive number, or blank for unlimited.');
      return;
    }
    setBusyType(rule.serviceType);
    setActionError(null);
    try {
      await apiPut(`/admin/service-rules/concurrency/${encodeURIComponent(rule.serviceType)}`, {
        maxConcurrentPerSpecialist,
      });
      reload();
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : 'Concurrency rule update failed');
    } finally {
      setBusyType(null);
    }
  };

  const showLoadingState = !rules && loading;

  return (
    <div>
      <div className="form-card" style={{ maxWidth: 'none', marginBottom: '1rem', background: 'var(--surface-muted, var(--surface))' }}>
        <StatusBadge label="Not yet enforced" tone="warn" />
        <p style={{ marginTop: '0.5rem', marginBottom: 0 }}>
          These caps are admin-visible/editable config only. Nothing in the matching or booking
          path currently reads <code>maxConcurrentPerSpecialist</code> — the only concurrency
          mechanic actually enforced today is the blocking/non-blocking (LIVE vs ASYNC)
          classification shown below.
        </p>
      </div>

      {error && <div className="error-banner">{error}</div>}
      {actionError && <div className="error-banner">{actionError}</div>}

      <DataTable
        id="service-rules-concurrency"
        rows={rules}
        loading={showLoadingState}
        empty="No service types found."
        noun="service types"
        getRowKey={(rule) => rule.serviceType}
        columns={[
          { key: 'serviceType', header: 'Service type', value: (rule) => rule.serviceType },
          {
            key: 'concurrencyClass',
            header: 'Concurrency class',
            value: (rule) => rule.defaultConcurrencyClass,
            filter: 'select',
            render: (rule) => (
              <StatusBadge
                label={rule.defaultConcurrencyClass}
                tone={rule.defaultConcurrencyClass === 'blocking' ? 'warn' : 'neutral'}
              />
            ),
          },
          {
            key: 'maxConcurrent',
            header: 'Max concurrent per specialist',
            render: (rule) => (
              <input
                type="number"
                min={1}
                step="1"
                placeholder="Unlimited"
                value={draftFor(rule)}
                onChange={(e) => setDrafts((prev) => ({ ...prev, [rule.serviceType]: e.target.value }))}
                style={{ width: '6rem' }}
              />
            ),
          },
          {
            key: 'actions',
            header: 'Actions',
            render: (rule) => (
              <button
                className="btn btn-primary"
                disabled={busyType === rule.serviceType}
                onClick={() => void saveRule(rule)}
              >
                Save
              </button>
            ),
          },
        ]}
      />
    </div>
  );
}

function EligibilityTab() {
  const { data, loading, error } = useApiResource(
    () => apiGet<EligibilityResponse>('/admin/service-rules/eligibility'),
    [],
  );

  return (
    <div>
      <div className="form-card" style={{ maxWidth: 'none', marginBottom: '1rem' }}>
        <StatusBadge label="Read-only" tone="neutral" />
        <p style={{ marginTop: '0.5rem', marginBottom: 0 }}>
          These are the current specialist-matching eligibility rules, hardcoded in the matching
          engine — not admin-editable. Shown here for visibility only.
        </p>
      </div>

      {error && <div className="error-banner">{error}</div>}

      {loading && !data ? (
        <div className="empty-state">Loading…</div>
      ) : !data || data.rules.length === 0 ? (
        <div className="empty-state">No eligibility rules found.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {data.rules.map((rule) => (
            <div key={rule.rule} className="form-card" style={{ maxWidth: 'none' }}>
              <h3 style={{ marginTop: 0 }}>{rule.rule}</h3>
              <p style={{ marginBottom: 0 }}>{rule.description}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
